import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from '../theme';
import { BentoCard, Spinner } from './shared';
import {
  listAnswerCatalog, listActs, upsertAct, deleteAct, AnswerCapabilityEntry, VnbAct, VnbActUpsert,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';

// <purpose>Antwort-Katalog tab — tenant-act CRUD (I-2 Task 9) over the vnb_acts-backed
// answer-capability registry. Tenant-owned rows (writeOwnership==='tenant') get an inline
// create/edit/delete surface; code-owned rows stay a read mirror with the lock affordance,
// since the system — not the tenant — owns their definition.</purpose>
// <dependencies>GET /manage/answer-catalog (listAnswerCatalog) for the read table + create-mode
// collision checks, GET /manage/acts (listActs) to seed an edit form from the FULL act row,
// POST /manage/acts (upsertAct), DELETE /manage/acts/{id} (deleteAct) — services/api.ts.
// BentoCard/Spinner (shared.tsx).</dependencies>
// <constraints>Code-owned rows render NO edit/delete controls — the lock stays. The table's
// thead/column-frame renders unconditionally; loading/empty/data states only ever swap tbody rows,
// so the card never changes height mid-fetch (Layout-Stability rule). Editing seeds the form from
// listActs() (the full VnbAct row) so beschreibung/scope/prioritaet/ausschlussBeispiele/vorlageText
// round-trip exactly — a save can never silently reset a field the FE didn't show. If the row isn't
// found there (should not happen in steady state; only a race with a concurrent delete), the form
// falls back to the lossy list-DTO seed and a hint warns the blank fields will be overwritten. The
// feedback banner is a fixed-height area that's always rendered, so a message appearing/clearing
// never shifts the form below it.</constraints>

// German stance labels — reviewed 2026-07-20 (register: sachlich-knapp, locked). Closed
// vocabulary from smarttix.core (vnb_acts.stance); unrecognized future values fail open to
// the raw code rather than hiding. Single source for BOTH the read column and the create/edit
// form's stance select — the form must never drift into its own copy of these labels.
const STANCE_LABELS: Record<AnswerCapabilityEntry['stance'], string> = {
  vorlage: 'Vorlage',
  grounded_rag: 'vom System beantwortet (verankert)',
  llm_wissen: 'KI-Wissen',
};

const STANCE_OPTIONS: Array<{ value: VnbActUpsert['stance']; label: string }> = [
  { value: 'llm_wissen', label: STANCE_LABELS.llm_wissen },
  { value: 'vorlage', label: STANCE_LABELS.vorlage },
  { value: 'grounded_rag', label: STANCE_LABELS.grounded_rag },
];

const SCOPES: Array<{ value: string; label: string }> = [
  { value: '', label: '(alle In-Domain-Fragen)' },
  { value: 'allgemeine_fachfrage', label: 'Allgemeine Fachfrage' },
  { value: 'unterlagen_anforderung', label: 'Unterlagen-Anforderung' },
];

// Fixed lock title for every code-owned row — explains WHY there's no edit control (the system
// owns this act's definition) rather than restating the stance column. First-draft German.
const CODE_OWNED_LOCK_TITLE = 'vom System beantwortet (verankert)';

const thStyle: React.CSSProperties = { padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500, textAlign: 'left' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 4, display: 'block',
};
function btnStyle(kind: 'primary' | 'danger' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500,
  };
  if (kind === 'primary') return { ...base, background: theme.colors.navy, color: theme.colors.white };
  // Full `border` (not `borderColor`) — the base already sets `border` as a shorthand, and mixing
  // shorthand + longhand for the same property across a rerender (danger ⇄ ghost swap) triggers a
  // React DOM styling warning.
  if (kind === 'danger') return { ...base, background: theme.colors.white, color: theme.colors.error, border: '1px solid #e0b4b4' };
  return { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}
function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: theme.borderRadius.sm, fontSize: 13,
    background: isError ? '#fbe9e9' : '#e9f6ec', color: isError ? theme.colors.error : '#1f7a3d',
  };
}

// Small padlock glyph — signals "systemgepflegt, schreibgeschützt" without needing an edit
// affordance to explain itself.
const LockIcon: React.FC = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="5" y="11" width="14" height="9" rx="2" stroke={theme.colors.navyMuted} strokeWidth="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke={theme.colors.navyMuted} strokeWidth="2" />
  </svg>
);

const linesToArr = (s: string): string[] => s.split('\n').map(x => x.trim()).filter(Boolean);
const arrToLines = (a: string[]): string => (a ?? []).join('\n');

const emptyForm: VnbActUpsert = {
  actId: '', name: '', beschreibung: '',
  triggerBeispiele: [], ausschlussBeispiele: [],
  scope: null, prioritaet: 100, stance: 'llm_wissen', vorlageText: null, aktiv: true,
};

const AntwortKatalog: React.FC = () => {
  const [entries, setEntries] = useState<AnswerCapabilityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state (create or edit). editingActId !== null ⇒ actId field is locked. fullySeeded
  // tracks whether the current edit form came from the FULL listActs() row (the happy path) or
  // the lossy list-DTO fallback (row-not-found race) — only the fallback shows the overwrite hint.
  const [form, setForm] = useState<VnbActUpsert>(emptyForm);
  const [triggerText, setTriggerText] = useState('');
  const [ausschlussText, setAusschlussText] = useState('');
  const [editingActId, setEditingActId] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [fullySeeded, setFullySeeded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setEntries(await listAnswerCatalog());
    } catch (e) {
      setError(formatBackendError(e, 'Laden des Antwort-Katalogs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(emptyForm); setTriggerText(''); setAusschlussText('');
    setEditingActId(null); setFullySeeded(true); setError(''); setSuccess('');
  };

  // Lossy fallback seed — only the list DTO's fields (see <constraints>: used when the full
  // row isn't found, e.g. a race with a concurrent delete of the same act).
  const seedFromListEntry = (e: AnswerCapabilityEntry) => {
    setForm({
      actId: e.id, name: e.displayName, beschreibung: '',
      triggerBeispiele: e.triggers, ausschlussBeispiele: [],
      scope: null, prioritaet: 100, stance: e.stance,
      vorlageText: null, aktiv: e.active,
    });
    setTriggerText(arrToLines(e.triggers));
    setAusschlussText('');
  };

  const seedFromFullAct = (a: VnbAct) => {
    setForm({
      actId: a.actId, name: a.name, beschreibung: a.beschreibung,
      triggerBeispiele: a.triggerBeispiele, ausschlussBeispiele: a.ausschlussBeispiele,
      scope: a.scope, prioritaet: a.prioritaet, stance: a.stance,
      vorlageText: a.vorlageText, aktiv: a.aktiv,
    });
    setTriggerText(arrToLines(a.triggerBeispiele));
    setAusschlussText(arrToLines(a.ausschlussBeispiele));
  };

  const startEdit = async (e: AnswerCapabilityEntry) => {
    setEditingActId(e.id);
    setError(''); setSuccess('');
    setEditLoading(true);
    setFullySeeded(false);
    seedFromListEntry(e); // provisional — avoids a blank flash while the full row loads
    try {
      const rows = await listActs();
      const full = rows.find(r => r.actId === e.id);
      if (full) {
        seedFromFullAct(full);
        setFullySeeded(true);
      }
      // else: row not found (race) — provisional lossy seed above stands, fallback hint shows.
    } catch {
      // network error fetching the full row — provisional lossy seed above stands.
    } finally {
      setEditLoading(false);
    }
  };

  const validationError = useMemo(() => {
    const trimmedActId = form.actId.trim();
    if (!trimmedActId) return 'act_id ist erforderlich.';
    if (!/^[a-z0-9_]+$/.test(trimmedActId)) return 'act_id: nur Kleinbuchstaben, Ziffern, Unterstrich.';
    // Create-mode only — in edit mode form.actId IS the (locked) existing id, so it always
    // "collides" with itself. upsertAct is an upsert: a create-mode actId matching an existing
    // row (code- or tenant-owned) would silently overwrite that OTHER act.
    if (!editingActId) {
      const collision = entries.find(en => en.id === trimmedActId);
      if (collision?.writeOwnership === 'code')
        return 'Diese act_id ist systembelegt (verankerte Antwort) — bitte eine eigene wählen.';
      if (collision)
        return 'Diese act_id existiert bereits — zum Bearbeiten die Zeile verwenden.';
    }
    if (!form.name.trim()) return 'Name ist erforderlich.';
    if (!form.beschreibung.trim()) return 'Beschreibung ist erforderlich.';
    if (form.stance === 'vorlage' && !(form.vorlageText ?? '').trim())
      return 'Vorlage-Text ist erforderlich, wenn Stance = Vorlage.';
    return '';
  }, [form, editingActId, entries]);

  const handleSave = async () => {
    if (validationError) { setError(validationError); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const vorlageTrimmed = (form.vorlageText ?? '').trim();
      await upsertAct({
        ...form,
        actId: form.actId.trim(),
        name: form.name.trim(),
        beschreibung: form.beschreibung.trim(),
        triggerBeispiele: linesToArr(triggerText),
        ausschlussBeispiele: linesToArr(ausschlussText),
        scope: form.scope ? form.scope : null,
        vorlageText: form.stance === 'vorlage' ? vorlageTrimmed : (vorlageTrimmed || null),
      });
      setSuccess(editingActId ? `Act „${form.actId}" aktualisiert.` : `Act „${form.actId}" angelegt.`);
      resetForm();
      await load();
    } catch (e) {
      setError(formatBackendError(e, 'Speichern des Acts'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (actId: string) => {
    setDeleting(actId); setError(''); setSuccess('');
    try {
      await deleteAct(actId);
      setSuccess(`Act „${actId}" gelöscht.`);
      setDeleteConfirm(null);
      if (editingActId === actId) resetForm();
      await load();
    } catch (e) {
      setError(formatBackendError(e, 'Löschen des Acts'));
    } finally {
      setDeleting(null);
    }
  };

  const set = <K extends keyof VnbActUpsert>(k: K, v: VnbActUpsert[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  return (
    <BentoCard title="Antwort-Katalog">
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        {/* Fixed-height, always-rendered — a message appearing/clearing swaps content inside this
            box rather than mounting/unmounting an element, so nothing below it ever jumps. */}
        <div data-testid="antwort-katalog-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch' }}>
          {error ? (
            <div style={{ ...feedbackStyle(true), width: '100%', boxSizing: 'border-box' }}>{error}</div>
          ) : success ? (
            <div style={{ ...feedbackStyle(false), width: '100%', boxSizing: 'border-box' }}>{success}</div>
          ) : null}
        </div>

        {/* thead/column-frame is unconditional — only tbody rows swap on loading/empty/data. */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.colors.bgLight}` }}>
              <th style={thStyle}>Antwort</th>
              <th style={thStyle}>Art</th>
              <th style={{ ...thStyle, width: 80 }}>Aktiv</th>
              <th style={{ ...thStyle, width: 160 }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: '24px 12px', textAlign: 'center' }}>
                <Spinner size={22} label="Lade Katalog..." />
              </td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '20px 12px', color: theme.colors.textSecondary, textAlign: 'center' }}>
                Kein Katalogeintrag gefunden.
              </td></tr>
            )}
            {!loading && entries.map(e => (
              <tr key={e.id} style={{ borderBottom: `1px solid ${theme.colors.bgLight}` }}>
                <td style={tdStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {e.writeOwnership === 'code' && (
                      <span title={CODE_OWNED_LOCK_TITLE} aria-label="Systemgepflegt (schreibgeschützt)" style={{ display: 'inline-flex' }}>
                        <LockIcon />
                      </span>
                    )}
                    {e.displayName}
                  </span>
                </td>
                <td style={tdStyle}>{STANCE_LABELS[e.stance] ?? e.stance}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span
                    title={e.active ? 'Aktiv' : 'Inaktiv'}
                    style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: e.active ? theme.colors.success : theme.colors.borderLight,
                    }}
                  />
                </td>
                <td style={tdStyle}>
                  {e.writeOwnership === 'tenant' && (
                    deleteConfirm === e.id ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleDelete(e.id)} style={btnStyle('danger')} disabled={deleting === e.id}>
                          {deleting === e.id ? 'Löscht…' : 'Löschen'}
                        </button>
                        <button onClick={() => setDeleteConfirm(null)} style={btnStyle('ghost')}>Abbrechen</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => startEdit(e)} style={btnStyle('ghost')}>Bearbeiten</button>
                        <button onClick={() => setDeleteConfirm(e.id)} style={btnStyle('ghost')}>Löschen</button>
                      </div>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Formular: eigener Abschnitt unter der Tabelle, damit die Tabelle nie springt. ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md, borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>
            {editingActId
              ? (editLoading ? `Lade Act „${editingActId}"…` : `Act bearbeiten: ${editingActId}`)
              : 'Neuen Act anlegen'}
          </span>

          {/* Only the fallback path (full row not found — see <constraints>) hits this; the happy
              path seeds every field from listActs() and needs no overwrite warning. */}
          {editingActId && !editLoading && !fullySeeded && (
            <p style={{ fontSize: 12, color: theme.colors.textSecondary, margin: 0 }}>
              Volldaten für diesen Act konnten nicht geladen werden — nicht angezeigte Felder werden
              beim Speichern überschrieben. Beschreibung erneut eingeben.
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
            <div>
              <label htmlFor="act-id" style={labelStyle}>act_id (Slug)</label>
              <input id="act-id" style={{ ...inputStyle, opacity: editingActId ? 0.6 : 1 }}
                value={form.actId} disabled={!!editingActId}
                placeholder="z.B. einspeise_verguetung"
                onChange={ev => set('actId', ev.target.value)} />
            </div>
            <div>
              <label htmlFor="act-name" style={labelStyle}>Name</label>
              <input id="act-name" style={inputStyle} value={form.name}
                placeholder="Anzeigename"
                onChange={ev => set('name', ev.target.value)} />
            </div>
          </div>

          <div>
            <label htmlFor="act-beschreibung" style={labelStyle}>Beschreibung (für Routing + als Leitplanke)</label>
            <textarea id="act-beschreibung" style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
              value={form.beschreibung}
              placeholder="Worum geht es? Diese Beschreibung nutzt die KI zum Zuordnen und als Fachkontext."
              onChange={ev => set('beschreibung', ev.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
            <div>
              <label htmlFor="act-trigger" style={labelStyle}>Passt bei (ein Beispiel pro Zeile)</label>
              <textarea id="act-trigger" style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                value={triggerText} placeholder={'Wie hoch ist meine Vergütung?\nWann bekomme ich Geld?'}
                onChange={ev => setTriggerText(ev.target.value)} />
            </div>
            <div>
              <label htmlFor="act-ausschluss" style={labelStyle}>Passt NICHT bei (ein Beispiel pro Zeile)</label>
              <textarea id="act-ausschluss" style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                value={ausschlussText} placeholder={'Wann wird meine Anlage angeschlossen?'}
                onChange={ev => setAusschlussText(ev.target.value)} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: theme.spacing.md }}>
            <div>
              <label htmlFor="act-scope" style={labelStyle}>Scope</label>
              <select id="act-scope" style={inputStyle} value={form.scope ?? ''}
                onChange={ev => set('scope', ev.target.value || null)}>
                {SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="act-stance" style={labelStyle}>Stance</label>
              <select id="act-stance" style={inputStyle} value={form.stance}
                onChange={ev => set('stance', ev.target.value as VnbActUpsert['stance'])}>
                {STANCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="act-prio" style={labelStyle}>Priorität</label>
              <input id="act-prio" type="number" style={inputStyle} value={form.prioritaet}
                onChange={ev => set('prioritaet', Number(ev.target.value) || 0)} />
            </div>
          </div>

          {form.stance === 'vorlage' && (
            <div>
              <label htmlFor="act-vorlage" style={labelStyle}>Vorlage-Text (geht wortwörtlich raus)</label>
              <textarea id="act-vorlage" style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }}
                value={form.vorlageText ?? ''}
                placeholder="Der exakte Antworttext, den der Kunde erhält."
                onChange={ev => set('vorlageText', ev.target.value)} />
            </div>
          )}

          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.aktiv} onChange={ev => set('aktiv', ev.target.checked)} />
              Aktiv
            </label>
            <div style={{ flex: 1 }} />
            {editingActId && <button onClick={resetForm} style={btnStyle('ghost')}>Abbrechen</button>}
            <button onClick={handleSave} style={btnStyle('primary')} disabled={saving || editLoading}>
              {saving ? 'Speichern…' : editingActId ? 'Act aktualisieren' : 'Act anlegen'}
            </button>
          </div>
        </div>
      </div>
    </BentoCard>
  );
};

export default AntwortKatalog;
